/**
 * User Profile API — backend calls for the V2 user profile page.
 *
 * READ: getuserprofile, att_user_roles, getuserlogs,
 *       dashboardhourchart, dashboardweekchart, eventReasonSummary, getlimitedevents
 * WRITE: user-manage-2 (cloud), user-manage (legacy) for edit/resetPassword/delete
 */

import { apiFetch, apiReport, apiSave, apiUserManageCloud, apiUserManageLegacy } from '../../../../services/api.js';
import { resetPassword as resetPasswordShared } from '../../api/usersApi.js';

// ---------------------------------------------------------------------------
// READ — Profile Data
// ---------------------------------------------------------------------------

export function fetchUserProfile(campusId, userId) {
    return apiFetch({
        procedure: 'getuserprofile',
        reqType: 'stored',
        payload: { user_filter: userId, campus_id: campusId },
    });
}

export function fetchUserRoles(campusId) {
    return apiFetch({
        table: 'att_user_roles',
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

export function fetchUserLogs(userId) {
    return apiFetch({
        procedure: 'getuserlogs',
        reqType: 'stored',
        pKey: 'user_id',
        pVal: userId,
    });
}

export function fetchPreformNotes(campusId) {
    return apiFetch({
        table: 'core_preform_event_notes',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// READ — Reporting Data
// ---------------------------------------------------------------------------

export function fetchHourChart(campusId, userId) {
    return apiReport({
        repType: 'dashboardhourchart',
        campusId,
        filters: { userFilter: userId.toString() },
    });
}

export function fetchWeekChart(campusId, userId) {
    return apiReport({
        repType: 'dashboardweekchart',
        campusId,
        filters: { userFilter: userId.toString() },
    });
}

export function fetchReasonSummary(campusId, userId, startDate, endDate) {
    return apiReport({
        repType: 'eventReasonSummary',
        campusId,
        startDate,
        endDate,
        filters: { userFilter: userId.toString() },
    });
}

export function fetchLimitedEvents(campusId, userId) {
    return apiFetch({
        procedure: 'getlimitedevents',
        reqType: 'stored',
        payload: { campus_id: campusId, user_id: userId },
    });
}

// ---------------------------------------------------------------------------
// WRITE — User Management
// ---------------------------------------------------------------------------

/**
 * Edit user profile (cloud or legacy).
 * @param {object} payload — new user data
 * @param {object} oldPayload — original user data (for audit)
 * @param {boolean} isCloud — campus is_cloud flag
 * @param {object} session — { userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export async function editUserProfile(payload, oldPayload, isCloud, session) {
    if (isCloud) {
        return apiUserManageCloud({
            payload: [{ action: 'update', ...payload, oldPayload }],
            clientId: session.clientId,
            userId: session.userId,
        });
    }
    return apiUserManageLegacy({
        payload: [{ Action: 'update', ...payload }],
        oldPayload: [oldPayload],
        userId: session.userId,
    });
}

/**
 * Reset user password.
 *
 * Delegates to the shared usersApi.resetPassword so the cloud/legacy payload
 * shaping (including the PascalCase key map and `Migrated: true` flag the
 * legacy backend requires) lives in one place. The previous in-line spread
 * implementation silently failed on legacy campuses because the user-profile
 * page passes snake_case keys that the legacy endpoint doesn't recognize.
 *
 * @param {object} payload  — { user_id, username, password, campus_id?, ... }
 * @param {boolean} _isCloud — accepted for back-compat; ignored. resetPassword
 *                             derives isCloud from getCampusConfig() itself.
 * @param {object} session   — { userId, clientId, campusId? }
 */
export async function resetUserPassword(payload, _isCloud, session) {
    return resetPasswordShared(
        {
            user_id: payload.user_id,
            username: payload.username,
            password: payload.password,
        },
        {
            ...session,
            campusId: session?.campusId ?? payload.campus_id,
        }
    );
}

/**
 * Delete user.
 */
export async function deleteUser(payload, isCloud, session) {
    if (isCloud) {
        return apiUserManageCloud({
            payload: [{ action: 'delete', ...payload }],
            clientId: session.clientId,
            userId: session.userId,
        });
    }
    return apiUserManageLegacy({
        payload: [{ Action: 'delete', ...payload }],
        userId: session.userId,
    });
}

/**
 * Add event note (for reporting tab).
 */
export async function addEventNote(eventId, campusId, note, userId, clientId) {
    return apiSave({
        table: 'core_event_notes',
        payload: [{ event_id: eventId, campus_id: campusId, note, modified_by: userId }],
        method: 'PUT',
        pKey: 'note_id',
        condition: 'primary',
        campusId,
        clientId,
        userId,
    });
}
